/**
 * PlanningIndicator — the platform's "thinking agent": a persistent status line
 * (our logo + a short line of text) shown across the whole workstream lifecycle,
 * NOT just while drafting. Per the product spec its text is ALWAYS italic +
 * muted grey (it never switches to upright/black), and it changes slowly so it
 * reads as the agent moving through work rather than a fast loop.
 *
 * Messages by phase:
 *   - drafting          → rotating "thinking" narration (live planning phase
 *                         when one has arrived, otherwise gentle filler)
 *   - awaiting_approval → "The draft plan is complete. Please review."
 *   - running           → "Currently running Step N."
 *   - completed / completed_with_flags → "The report is ready for your review."
 *   - other terminal (failed/cancelled/infeasible) → nothing (header covers it)
 *
 * The activity cues — the slowly turning logo and the three bouncing dots —
 * show ONLY while the agent is actively working (drafting / running), and stop
 * while it is idle waiting on the user (awaiting_approval) or finished
 * (terminal). Both are driven by the same condition so they never disagree.
 */
import { useEffect, useMemo, useState } from 'react';
import type { WorkstreamEvent } from '../../types/workstream';

const PHASE_COPY: Record<string, string> = {
  understanding: 'Reading your request…',
  probing: 'Searching the regulatory corpora…',
  reviewing: 'Reviewing what came back…',
  finalizing: 'Settling on an approach…',
  plan_review: 'Checking the draft plan…',
  writing: 'Writing the plan…',
};

// Gentle, honest filler — no "bamboozling"/"tricking" flavour (product spec).
const FILLER = [
  'Thinking through the approach…',
  'Drawing up the plan…',
  'Consulting the label archives…',
  'Sequencing the steps…',
];

/** How long a delivered phase stays on screen before filler resumes. */
const PHASE_TTL_MS = 12000;
/** How often the filler line rotates / staleness is re-checked. Deliberately
 *  slow so the line reads as steady progress, not a fast loop. */
const FILLER_ROTATE_MS = 6000;

const TERMINAL_SUCCESS = new Set(['completed', 'completed_with_flags', 'complete_with_flags']);

export interface PlanningIndicatorProps {
  /** Tail events (drafting narration is derived from ``planning`` envelopes). */
  events: WorkstreamEvent[];
  /** Live workstream status; defaults to ``drafting`` for the standalone case. */
  status?: string | null;
  /** Ordinal of the step currently running (for the ``running`` message). */
  currentStepOrdinal?: number | null;
  /** True once the plan has FINISHED appearing on screen (its staged reveal is
   *  done). While false at ``awaiting_approval`` the agent stays in a "writing"
   *  state so the steps read as showing up in real time BEFORE it declares the
   *  plan complete. */
  planReady?: boolean;
}

export function PlanningIndicator({ events, status = 'drafting', currentStepOrdinal = null, planReady = false }: PlanningIndicatorProps) {
  const [now, setNow] = useState(() => Date.now());
  const [fillerIndex, setFillerIndex] = useState(0);

  const latest = useMemo(() => {
    for (let i = events.length - 1; i >= 0; i -= 1) {
      const e = events[i];
      if (e && e.event === 'planning') return e;
    }
    return null;
  }, [events]);

  // One slow interval drives both filler rotation and the staleness re-check.
  useEffect(() => {
    const id = setInterval(() => {
      setNow(Date.now());
      setFillerIndex((i) => (i + 1) % FILLER.length);
    }, FILLER_ROTATE_MS);
    return () => clearInterval(id);
  }, []);

  const drafting = status === 'drafting' || status == null;
  const phase = latest ? String(latest.payload['phase'] ?? '') : '';
  const emittedAt = latest ? Date.parse(latest.emitted_at) : NaN;
  const fresh = Boolean(phase) && Number.isFinite(emittedAt) && now - emittedAt < PHASE_TTL_MS;
  const detail = latest ? latest.payload['detail'] : undefined;

  // A non-drafting phase renders a single fixed line (no filler rotation); the
  // activity cues still run whenever that phase is itself active work.
  if (!drafting) {
    let text: string | null = null;
    let active = false;
    if (status === 'awaiting_approval') {
      if (planReady) {
        text = 'The draft plan is complete. Please review.';
      } else {
        // Still "thinking" while the steps stagger in — the plan is not
        // declared complete until its reveal has finished (planReady).
        text = 'Writing the plan…';
        active = true;
      }
    } else if (status === 'running') {
      text = currentStepOrdinal != null ? `Currently running Step ${currentStepOrdinal}.` : 'Starting the run…';
      active = true;
    } else if (TERMINAL_SUCCESS.has(String(status))) {
      text = 'The report is ready for your review.';
    }
    if (text === null) return null; // failed/cancelled/infeasible — header covers it.
    return (
      <div className="flex items-center gap-3" data-testid="planning-indicator">
        <Logo spinning={active} />
        <p className="text-sm" aria-live="polite">
          <span className="italic text-slate-400" data-testid="planning-status">{text}</span>
        </p>
        {active && <Dots />}
      </div>
    );
  }

  return (
    <div className="flex items-center gap-3" data-testid="planning-indicator">
      {/* Drafting is always active work, so the mark always turns here. */}
      <Logo spinning />
      <p className="text-sm" aria-live="polite">
        {fresh ? (
          // Italic + grey like the filler — the line never switches to upright/black.
          <span className="italic text-slate-400" data-testid="planning-phase">
            {PHASE_COPY[phase] ?? 'Working…'}
            {typeof detail === 'string' && detail ? ` ${detail}` : ''}
          </span>
        ) : (
          <span className="italic text-slate-400" data-testid="planning-filler">
            {FILLER[fillerIndex]}
          </span>
        )}
      </p>
      <Dots />
    </div>
  );
}

/** The brand mark. It turns slowly (3s) whenever the agent is actively working —
 *  the same condition that shows the dots — and sits still once it is idle
 *  waiting on the user or finished. `motion-safe:` gated, so reduced-motion
 *  users get a static mark and rely on the text line instead. */
function Logo({ spinning }: { spinning: boolean }) {
  return (
    <img
      src="/logo.svg"
      alt=""
      aria-hidden="true"
      className={`h-8 w-8 ${spinning ? 'motion-safe:animate-spin' : ''}`}
      style={spinning ? { animationDuration: '3s' } : undefined}
    />
  );
}

/** Three staggered bouncing dots — the "actively working" activity cue. */
function Dots() {
  return (
    <span aria-hidden="true" data-testid="planning-dots" className="ml-1 inline-flex items-end gap-1">
      {[0, 1, 2].map((i) => (
        <span
          key={i}
          className="h-1.5 w-1.5 rounded-full bg-slate-400 motion-safe:animate-bounce"
          style={{ animationDelay: `${i * 150}ms` }}
        />
      ))}
    </span>
  );
}
